import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Copy,
  Download,
  MoreVertical,
  PackagePlus,
  Pencil,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Trash2,
  Upload,
  X,
} from "lucide-react";

import { api } from "../../services/api";
import {
  PaginationControls,
  StatusPill,
} from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { useServerPagination } from "./useServerPagination";
import { ToastStack, useToast } from "./useToast";
import { resolveProductImage } from "../../utils/productAssets";

export const PRODUCT_FILTERS = [
  "All",
  "Low stock",
  "Active",
  "Draft",
  "Inactive",
  "eBay",
  "Bobbin USA",
];

export const PRODUCT_FILTER_TO_PARAM = {
  "Low stock": { lowStock: "true" },
  Active: { status: "active" },
  Draft: { status: "draft" },
  Inactive: { status: "inactive" },
  eBay: { channel: "ebay" },
  "Bobbin USA": { channel: "bobbinusa" },
};

export const PRODUCT_CHANNEL_LABELS = {
  ebay: "eBay",
};

export function useDebounce(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

function normalizeChannels(channels) {
  if (!Array.isArray(channels)) return [];

  return channels
    .map((channel) => {
      if (typeof channel === "string") return channel;
      return channel?.channel || channel?.name;
    })
    .filter(Boolean)
    .map((channel) => String(channel).toLowerCase());
}

export function normalizeProduct(raw = {}) {
  return {
    id: raw._id || raw.id,
    raw,
    image: raw.image || "",
    sku: raw.sku || "N/A",
    name: raw.name || raw.title || "Untitled Product",
    brandLine: raw.brandLine || raw.brand || "General",
    category: raw.category || "Industrial Sewing Parts",
    price: Number(raw.price || 0),
    compareAtPrice: Number(raw.compareAtPrice || 0),
    stock: Number(raw.stock || 0),
    lowStockThreshold: Number(raw.lowStockThreshold || 10),
    status: raw.status || "draft",
    channels: normalizeChannels(raw.channels),
    ebayCategoryId: raw.ebay?.categoryId || "",
    ebayCategoryPath: raw.ebay?.categoryPath || "",
    supplier:
      typeof raw.supplier === "string"
        ? raw.supplier
        : raw.supplier?.name || "Not linked",
    velocity: Number(raw.salesVelocity30d || 0),
    soldCount: Number(raw.soldCount || 0),
    ebayStatus: raw.ebay?.lastSyncStatus || "not_synced",
  };
}

export function getErrorMessage(err, fallback = "Something went wrong") {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  );
}

export function formatChannels(channels = []) {
  return channels.map((channel) => PRODUCT_CHANNEL_LABELS[channel] || channel).join(", ");
}

export function ProductFilterBar({ active, onFilterChange, search, onSearchChange }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b border-gray-100 px-5 py-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#4391d1]">
            <SlidersHorizontal size={18} />
          </span>
          <div>
            <h2 className="text-sm font-black uppercase tracking-wide text-gray-900">Find products</h2>
            <p className="mt-1 text-xs font-semibold text-gray-400">
              Filter inventory by stock, status, channel, SKU, brand, or product name.
            </p>
          </div>
        </div>

        <div className="relative w-full xl:max-w-md">
          <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={17} />
          <input
            className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3 pl-11 pr-12 text-sm font-semibold outline-none transition focus:border-[#4391d1] focus:bg-white focus:ring-4 focus:ring-blue-100"
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search SKU, product, brand..."
            type="search"
            value={search}
          />
          {search && (
            <button
              aria-label="Clear product search"
              className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-gray-400 transition hover:bg-white hover:text-gray-700"
              onClick={() => onSearchChange("")}
              type="button"
            >
              <X size={15} />
            </button>
          )}
        </div>
      </div>

      <div className="px-5 py-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="text-[11px] font-black uppercase tracking-wide text-gray-400">Quick filters</span>
          <span className="rounded-full bg-blue-50 px-3 py-1 text-[11px] font-black uppercase text-[#1278b5]">{active}</span>
        </div>

        <div className="flex flex-wrap gap-2">
          {PRODUCT_FILTERS.map((filter) => (
            <button
              className={`rounded-xl px-3 py-2 text-xs font-black transition-colors ${
                active === filter
                  ? "bg-[#4391d1] text-white shadow-sm"
                  : "border border-gray-200 bg-gray-50 text-gray-600 hover:border-[#4391d1] hover:bg-white hover:text-[#4391d1]"
              }`}
              key={filter}
              onClick={() => onFilterChange(filter)}
              type="button"
            >
              {filter}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuickFilterDropdown({ active, onFilterChange }) {
  const [open, setOpen] = useState(false);

  const close = () => setOpen(false);

  return (
    <div
      className="relative inline-flex"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          close();
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          close();
        }
      }}
    >
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]"
        onClick={() => setOpen((current) => !current)}
        type="button"
      >
        <SlidersHorizontal size={14} />
        Quick filters
        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-black uppercase text-[#1278b5]">
          {active}
        </span>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1.5 min-w-52 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 text-left shadow-xl shadow-gray-900/10" role="menu">
          {PRODUCT_FILTERS.map((filter) => (
            <button
              className={`flex w-full items-center justify-between px-3 py-2 text-xs font-bold transition hover:bg-blue-50 hover:text-[#1278b5] ${
                active === filter ? "bg-blue-50 text-[#1278b5]" : "text-gray-700"
              }`}
              key={filter}
              onClick={() => {
                onFilterChange(filter);
                close();
              }}
              role="menuitem"
              type="button"
            >
              <span>{filter}</span>
              {active === filter && <span className="text-[10px] font-black uppercase">Active</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PriceUpdatePanel({ percent, onPercentChange, onApply }) {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="rounded-xl bg-white p-2 text-amber-600">
          <AlertTriangle size={18} />
        </span>

        <div>
          <h2 className="text-sm font-black uppercase text-gray-900">
            One-click price update
          </h2>

          <p className="mt-1 text-xs font-semibold leading-5 text-gray-600">
            Apply yearly or quarterly genuine parts increases to every product
            in a brand line.
          </p>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <input
          className="w-24 rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm font-bold outline-none focus:border-amber-400"
          min="1"
          max="100"
          onChange={(event) => onPercentChange(event.target.value)}
          type="number"
          value={percent}
        />

        <button
          className="flex-1 rounded-xl bg-gray-950 px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-80"
          onClick={onApply}
          type="button"
        >
          Increase Juki Line {percent}%
        </button>
      </div>
    </div>
  );
}

function InventoryAdjustCell({ productId, value, onChange, onApply }) {
  return (
    <div className="mt-1 flex gap-1">
      <input
        className="min-h-7 h-7 w-12 rounded-md border border-gray-200 px-1.5 text-[10px] font-bold leading-none outline-none focus:border-[#4391d1]"
        onChange={(event) =>
          onChange((current) => ({
            ...current,
            [productId]: event.target.value,
          }))
        }
        placeholder="+/-"
        type="number"
        value={value || ""}
      />

      <button
        className="h-7 rounded-md bg-gray-950 px-1.5 text-[9px] font-bold leading-none text-white transition-opacity hover:opacity-80 disabled:opacity-40"
        disabled={!value}
        onClick={onApply}
        type="button"
      >
        Apply
      </button>
    </div>
  );
}

function MarketplaceStatus({ product, connectedChannels }) {
  const connected = new Set(connectedChannels);

  const tone = (status) => {
    if (status === "active" || status === "synced") return "text-emerald-600";
    if (status === "error") return "text-red-500";
    if (status === "pending") return "text-amber-500";
    return "text-gray-400";
  };

  return (
    <div className="space-y-1 text-[11px] font-bold">
      {connected.has("ebay") && (
        <div className={tone(product.ebayStatus)}>
          <span className="inline-flex rounded-md bg-gray-100 px-2 py-1 font-black text-gray-600">
            eBay: {product.ebayStatus}
          </span>
          {!product.ebayCategoryId && (
            <p className="mt-1 font-black text-amber-600">
              Category required
            </p>
          )}
          {product.ebayCategoryPath && (
            <p className="mt-1 max-w-[150px] truncate font-semibold text-gray-400">
              {product.ebayCategoryPath}
            </p>
          )}
        </div>
      )}

      {!connected.has("ebay") && (
        <div className="text-gray-400">Not connected</div>
      )}
    </div>
  );
}

function ProductActionDropdown({
  product,
  uploadTargets,
  uploadingKey,
  onDuplicate,
  onEdit,
  onDelete,
  onUploadProduct,
}) {
  const [open, setOpen] = useState(false);
  const uploading = Boolean(uploadingKey);

  const close = () => setOpen(false);

  const runAction = (handler) => {
    close();
    handler();
  };

  return (
    <div
      className="relative inline-flex justify-end"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          close();
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          close();
        }
      }}
    >
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-[#4391d1] hover:text-[#4391d1] focus:border-[#4391d1] focus:outline-none focus:ring-4 focus:ring-blue-100"
        onClick={() => setOpen((current) => !current)}
        type="button"
      >
        <MoreVertical size={15} />
        <span className="sr-only">Product actions</span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-1.5 w-52 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 text-left shadow-xl shadow-gray-900/10"
          role="menu"
        >
          {uploadTargets.length ? (
            uploadTargets.map((target) => {
              const key = `${product.id}:${target.key}`;
              const isLoading = uploadingKey === key;

              return (
                <button
                  className="flex w-full items-center gap-2 px-3 py-2 text-xs font-bold text-gray-700 transition hover:bg-blue-50 hover:text-[#1278b5] disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={uploading}
                  key={target.key}
                  onClick={() =>
                    runAction(() => onUploadProduct(product, target))
                  }
                  role="menuitem"
                  type="button"
                >
                  {isLoading ? (
                    <RefreshCw size={14} className="animate-spin" />
                  ) : (
                    <Upload size={14} />
                  )}
                  {isLoading ? "Uploading..." : `Upload to ${target.label}`}
                </button>
              );
            })
          ) : (
            <div className="px-3 py-2 text-xs font-bold text-gray-400">
              Connect eBay before upload
            </div>
          )}

          <div className="my-1 border-t border-gray-100" />

          <button
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-bold text-gray-700 transition hover:bg-gray-50 hover:text-[#4391d1]"
            onClick={() => runAction(() => onEdit(product))}
            role="menuitem"
            type="button"
          >
            <Pencil size={14} />
            Edit product
          </button>

          <button
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-bold text-gray-700 transition hover:bg-blue-50 hover:text-[#1278b5]"
            onClick={() => runAction(() => onDuplicate(product))}
            role="menuitem"
            type="button"
          >
            <Copy size={14} />
            Duplicate product
          </button>

          <button
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-bold text-red-600 transition hover:bg-red-50"
            onClick={() => runAction(() => onDelete(product))}
            role="menuitem"
            type="button"
          >
            <Trash2 size={14} />
            Delete product
          </button>
        </div>
      )}
    </div>
  );
}

export function BulkUploadBar({
  selectedCount,
  uploadTargets,
  bulkUploading,
  onBulkUpload,
  onClearSelection,
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-gray-100 bg-gray-50 px-6 py-4 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h3 className="text-sm font-black text-gray-900">
          Bulk Marketplace Upload
        </h3>

        <p className="mt-0.5 text-xs font-semibold text-gray-500">
          {selectedCount > 0
            ? `${selectedCount} product${selectedCount !== 1 ? "s" : ""} selected`
            : "Select products from the table to upload in bulk."}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {uploadTargets.map((target) => (
          <button
            className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-black text-white transition-opacity hover:opacity-80 disabled:opacity-40 ${
              target.key === "both" ? "bg-emerald-600" : "bg-[#4391d1]"
            }`}
            disabled={!selectedCount || Boolean(bulkUploading)}
            key={target.key}
            onClick={() => onBulkUpload(target)}
            type="button"
          >
            {bulkUploading === target.key ? (
              <RefreshCw size={14} className="animate-spin" />
            ) : (
              <Upload size={14} />
            )}

            {bulkUploading === target.key
              ? "Uploading..."
              : `Upload to ${target.label}`}
          </button>
        ))}

        <button
          className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-black text-gray-600 hover:border-gray-400 disabled:opacity-40"
          disabled={!selectedCount || Boolean(bulkUploading)}
          onClick={onClearSelection}
          type="button"
        >
          Clear
        </button>
      </div>
    </div>
  );
}

function EbayCategorySyncPanel({ job }) {
  if (!job) return null;

  const total = Number(job.total || 0);
  const processed = Number(job.processed || 0);
  const failed = Number(job.failed || 0);
  const isRunning = ["pending", "running"].includes(job.status);
  const percent =
    total > 0
      ? Math.min(100, Math.round((processed / total) * 100))
      : job.status === "completed"
      ? 100
      : 0;
  const statusClass =
    job.status === "failed"
      ? "border-red-200 bg-red-50"
      : job.status === "paused"
      ? "border-amber-200 bg-amber-50"
      : failed > 0 && job.status === "completed"
      ? "border-amber-200 bg-amber-50"
      : "border-blue-100 bg-blue-50";

  return (
    <div className={`border-b px-6 py-4 ${statusClass}`}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            {isRunning && <RefreshCw className="h-4 w-4 animate-spin text-[#4391d1]" />}
            <h3 className="text-sm font-black text-gray-900">
              eBay Category Sync
            </h3>
            <StatusPill
              tone={
                job.status === "completed"
                  ? failed
                    ? "warn"
                    : "good"
                  : job.status === "paused"
                  ? "warn"
                  : job.status === "failed"
                  ? "bad"
                  : "info"
              }
            >
              {job.status}
            </StatusPill>
          </div>

          <p className="mt-1 text-xs font-semibold text-gray-600">
            {job.message || "Syncing eBay categories."}
          </p>

          {job.currentSku && (
            <p className="mt-1 text-xs font-black text-gray-500">
              Current SKU: {job.currentSku}
            </p>
          )}
        </div>

        <div className="grid grid-cols-4 gap-2 text-center text-xs font-black text-gray-700 sm:min-w-[360px]">
          <div className="rounded-lg bg-white px-3 py-2">
            <p className="text-gray-400">Done</p>
            <p>{processed}/{total}</p>
          </div>
          <div className="rounded-lg bg-white px-3 py-2">
            <p className="text-gray-400">Synced</p>
            <p>{job.synced || 0}</p>
          </div>
          <div className="rounded-lg bg-white px-3 py-2">
            <p className="text-gray-400">Skipped</p>
            <p>{job.skipped || 0}</p>
          </div>
          <div className="rounded-lg bg-white px-3 py-2">
            <p className="text-gray-400">Failed</p>
            <p>{failed}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 h-2 overflow-hidden rounded-full bg-white">
        <div
          className={`h-full rounded-full ${
            job.status === "failed"
              ? "bg-red-500"
              : job.status === "paused"
              ? "bg-amber-500"
              : "bg-[#4391d1]"
          }`}
          style={{ width: `${percent}%` }}
        />
      </div>

      {Array.isArray(job.errors) && job.errors.length > 0 && (
        <div className="mt-3 space-y-1 text-xs font-semibold text-gray-600">
          {job.errors.slice(-3).map((error) => (
            <p key={`${error.sku}-${error.message}`}>
              <span className="font-black">{error.sku}:</span> {error.message}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export function ProductRow({
  product,
  adjustment,
  checked,
  connectedChannels,
  uploadTargets,
  uploadingKey,
  onToggleSelect,
  onAdjustmentChange,
  onAdjustInventory,
  onDuplicate,
  onEdit,
  onDelete,
  onUploadProduct,
}) {
  const stock = Number(product?.stock || 0);
  const lowStockThreshold = Number(product?.lowStockThreshold || 10);
  const isLowStock = stock <= lowStockThreshold;

  return (
    <tr className="text-xs text-gray-600 transition-colors hover:bg-gray-50/70">
      <td className="px-4 py-2.5 align-middle">
        <input
          checked={checked}
          className="h-3.5 w-3.5 rounded border-gray-300 text-[#4391d1] focus:ring-[#4391d1]"
          onChange={() => onToggleSelect(product.id)}
          type="checkbox"
        />
      </td>

      <td className="min-w-72 px-4 py-2.5 align-middle">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
            <img
              alt={product.name}
              className="h-full w-full object-contain p-1"
              loading="lazy"
              src={resolveProductImage(product.image)}
            />
          </div>

          <div className="min-w-0">
            <div className="max-w-[360px] truncate font-black text-gray-900">{product.name}</div>

            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] font-bold text-gray-400">
              <span>SKU: {product.sku}</span>
              <span>Sold: {product.soldCount}</span>
              <span>Velocity: {product.velocity}</span>
            </div>
          </div>
        </div>
      </td>

      <td className="min-w-40 px-4 py-2.5 align-middle">
        <MarketplaceStatus
          product={product}
          connectedChannels={connectedChannels}
        />
      </td>

      <td className="px-4 py-2.5 align-middle">
        <div className="space-y-1">
          <StatusPill tone={product.status === "active" ? "good" : "neutral"}>
            {product.status}
          </StatusPill>
          <div className="max-w-32 truncate text-[11px] font-bold text-gray-500">{product.brandLine}</div>
          <div className="max-w-32 truncate text-[11px] text-gray-400">{product.category}</div>
        </div>
      </td>

      <td className="w-24 whitespace-nowrap px-3 py-2.5 align-middle">
        <div className="font-bold text-gray-900">
          ${Number(product.price || 0).toFixed(2)}
        </div>

        {Number(product.compareAtPrice || 0) > 0 && (
          <div className="text-[11px] text-gray-400 line-through">
            ${Number(product.compareAtPrice || 0).toFixed(2)}
          </div>
        )}
      </td>

      <td className="whitespace-nowrap px-4 py-2.5 align-middle">
        <span
          className={
            isLowStock ? "font-black text-red-600" : "font-bold text-gray-800"
          }
        >
          {stock}
        </span>

        {isLowStock && (
          <div className="text-[11px] font-bold text-red-400">
            Low: {lowStockThreshold}
          </div>
        )}

        <InventoryAdjustCell
          productId={product.id}
          value={adjustment}
          onChange={onAdjustmentChange}
          onApply={() => onAdjustInventory(product)}
        />
      </td>

      <td className="w-12 px-4 py-2.5 text-right align-middle">
        <ProductActionDropdown
          product={product}
          uploadTargets={uploadTargets}
          uploadingKey={uploadingKey}
          onDuplicate={onDuplicate}
          onEdit={onEdit}
          onDelete={onDelete}
          onUploadProduct={onUploadProduct}
        />
      </td>
    </tr>
  );
}

export default function ProductAndInventory() {
  const navigate = useNavigate();

  const [activeFilter, setActiveFilter] = useState("All");
  const [pricePercent, setPricePercent] = useState(10);
  const [adjustments, setAdjustments] = useState({});
  const [connectedChannels, setConnectedChannels] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState(() => new Set());
  const [uploadingKey, setUploadingKey] = useState("");
  const [bulkUploading, setBulkUploading] = useState("");
  const [categorySyncJob, setCategorySyncJob] = useState(null);

  const { dismiss, toast, toasts } = useToast();

  const { page, pageSize, setPage, setPageSize, resetPage } =
    useServerPagination(25);

  useEffect(() => {
    resetPage();
  }, [activeFilter, resetPage]);

  const queryParams = useMemo(() => {
    const params = {
      page: String(page),
      limit: String(pageSize),
    };

    const filterParam = PRODUCT_FILTER_TO_PARAM[activeFilter];

    if (filterParam) {
      Object.assign(params, filterParam);
    }

    return params;
  }, [page, pageSize, activeFilter]);

  const loadConnectedChannels = useCallback(async () => {
    try {
      const response = await api.get("/channels");

      const channels = Array.isArray(response?.channels)
        ? response.channels
        : [];

      const connected = channels
        .filter((channel) => channel.status === "connected")
        .map((channel) => channel.name);

      setConnectedChannels(connected);
    } catch (err) {
      setConnectedChannels([]);
    }
  }, []);

  useEffect(() => {
    loadConnectedChannels();
  }, [loadConnectedChannels]);

  const loadProducts = useCallback(async () => {
    const qs = new URLSearchParams(queryParams).toString();
    const response = await api.get(`/products?${qs}`);

    const apiPagination = response?.pagination || {};

    return {
      products: Array.isArray(response?.products)
        ? response.products.map(normalizeProduct)
        : [],

      stats: {
        lowStockCount: 0,
        ...(response?.stats || {}),
      },

      pagination: {
        page: Number(apiPagination.page || page),
        pageSize: Number(apiPagination.limit || apiPagination.pageSize || pageSize),
        totalItems: Number(apiPagination.total || apiPagination.totalItems || 0),
        totalPages: Number(apiPagination.totalPages || apiPagination.pages || 1),
        hasPrevPage: Boolean(apiPagination.hasPrevPage),
        hasNextPage: Boolean(apiPagination.hasNextPage),
        setPage,
        setPageSize,
      },
    };
  }, [queryParams, page, pageSize, setPage, setPageSize]);

  const { data, error, loading, reload, setData } = useAdminResource(
    loadProducts,
    {
      products: [],
      stats: {
        lowStockCount: 0,
      },
      pagination: {
        page,
        pageSize,
        totalItems: 0,
        totalPages: 1,
        hasPrevPage: false,
        hasNextPage: false,
        setPage,
        setPageSize,
      },
    }
  );

  const products = useMemo(() => {
    return Array.isArray(data?.products) ? data.products : [];
  }, [data?.products]);

  const uploadTargets = useMemo(() => {
    const connected = new Set(connectedChannels);
    const targets = [];

    if (connected.has("ebay")) {
      targets.push({
        key: "ebay",
        label: "eBay",
        channels: ["ebay"],
      });
    }

    return targets;
  }, [connectedChannels]);

  const pagination = useMemo(() => {
    const apiPagination = data?.pagination || {};

    return {
      page: Number(apiPagination.page || page),
      pageSize: Number(apiPagination.pageSize || apiPagination.limit || pageSize),
      totalItems: Number(apiPagination.totalItems || apiPagination.total || 0),
      totalPages: Number(apiPagination.totalPages || apiPagination.pages || 1),
      hasPrevPage: Boolean(apiPagination.hasPrevPage),
      hasNextPage: Boolean(apiPagination.hasNextPage),
      disabled: loading,
      setPage,
      setPageSize,
    };
  }, [data?.pagination, loading, page, pageSize, setPage, setPageSize]);

  useEffect(() => {
    if (error && !loading) {
      toast(`Could not load products: ${error}`, "error");
    }
  }, [error, loading, toast]);

  useEffect(() => {
    setSelectedProductIds((current) => {
      const visibleIds = new Set(products.map((product) => product.id));
      return new Set([...current].filter((id) => visibleIds.has(id)));
    });
  }, [products]);

  const selectedProducts = useMemo(() => {
    return products.filter((product) => selectedProductIds.has(product.id));
  }, [products, selectedProductIds]);

  const allCurrentPageSelected = useMemo(() => {
    return (
      products.length > 0 &&
      products.every((product) => selectedProductIds.has(product.id))
    );
  }, [products, selectedProductIds]);

  const toggleSelectProduct = useCallback((productId) => {
    setSelectedProductIds((current) => {
      const next = new Set(current);

      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }

      return next;
    });
  }, []);

  const toggleSelectCurrentPage = useCallback(() => {
    setSelectedProductIds((current) => {
      const next = new Set(current);

      if (products.length && products.every((product) => next.has(product.id))) {
        products.forEach((product) => next.delete(product.id));
      } else {
        products.forEach((product) => next.add(product.id));
      }

      return next;
    });
  }, [products]);

  const clearSelection = useCallback(() => {
    setSelectedProductIds(new Set());
  }, []);

  const editProduct = useCallback(
    (product) => {
      navigate(`/dashboard/products/${product.id}/edit`);
    },
    [navigate]
  );

  const duplicateProduct = useCallback(
    async (product) => {
      if (!window.confirm(`Duplicate product ${product.sku}? A draft copy will be created.`)) {
        return;
      }

      try {
        const response = await api.post(`/products/${product.id}/duplicate`, {});
        const duplicated = response?.product;

        if (!duplicated?._id) {
          throw new Error("Duplicate response was invalid.");
        }

        toast(`Created draft copy ${duplicated.sku}`, "success");
        navigate(`/dashboard/products/${duplicated._id}/edit`);
      } catch (err) {
        toast(getErrorMessage(err, "Could not duplicate product"), "error");
      }
    },
    [navigate, toast]
  );

  const updateProductInTable = useCallback(
    (rawProduct) => {
      if (!rawProduct) return;

      const updated = normalizeProduct(rawProduct);

      setData((current) => ({
        ...current,
        products: Array.isArray(current.products)
          ? current.products.map((item) =>
              item.id === updated.id ? updated : item
            )
          : [],
      }));
    },
    [setData]
  );

  const deleteProduct = useCallback(
    async (product) => {
      if (
        !window.confirm(
          `Delete product ${product.sku}? This removes it from the website catalog.`
        )
      ) {
        return;
      }

      try {
        await api.delete(`/products/${product.id}`);
        toast(`Deleted product ${product.sku}`, "success");

        if (products.length === 1 && pagination.page > 1) {
          setPage((currentPage) => Math.max(1, currentPage - 1));
        } else {
          await reload();
        }
      } catch (err) {
        toast(getErrorMessage(err, "Could not delete product"), "error");
      }
    },
    [products.length, pagination.page, reload, setPage, toast]
  );

  const uploadOneChannel = useCallback(async (product, channel) => {
    if (channel === "ebay") {
      if (!product.ebayCategoryId) {
        throw new Error(
          `${product.sku} needs an eBay category before upload. Open Edit Product, search/select the eBay category, save, then upload again.`
        );
      }

      return api.post("/channels/ebay/products/upload", {
        productId: product.id,
      });
    }

    throw new Error(`Unsupported channel: ${channel}`);
  }, []);

  const uploadProductToTarget = useCallback(
    async (product, target) => {
      const key = `${product.id}:${target.key}`;

      try {
        setUploadingKey(key);

        const results = await Promise.allSettled(
          target.channels.map((channel) => uploadOneChannel(product, channel))
        );

        const successResults = results.filter(
          (result) => result.status === "fulfilled"
        );

        const failedResults = results.filter(
          (result) => result.status === "rejected"
        );

        const updatedProduct =
          successResults.find((result) => result.value?.product)?.value
            ?.product || null;

        if (updatedProduct) {
          updateProductInTable(updatedProduct);
        }

        if (failedResults.length) {
          const firstError = failedResults[0]?.reason;
          toast(
            getErrorMessage(
              firstError,
              `${product.sku} upload failed for ${target.label}`
            ),
            "error"
          );
        }

        if (successResults.length) {
          toast(
            `${product.sku} uploaded to ${formatChannels(target.channels)}`,
            "success"
          );
        }
      } catch (err) {
        toast(getErrorMessage(err, "Could not upload product"), "error");
      } finally {
        setUploadingKey("");
      }
    },
    [toast, uploadOneChannel, updateProductInTable]
  );

  const bulkUploadToTarget = useCallback(
    async (target) => {
      if (!selectedProducts.length) {
        toast("Please select at least one product", "error");
        return;
      }

      if (
        !window.confirm(
          `Upload ${selectedProducts.length} selected product${
            selectedProducts.length !== 1 ? "s" : ""
          } to ${target.label}?`
        )
      ) {
        return;
      }

      try {
        setBulkUploading(target.key);

        let successCount = 0;
        let failCount = 0;

        for (const product of selectedProducts) {
          const results = await Promise.allSettled(
            target.channels.map((channel) => uploadOneChannel(product, channel))
          );

          const hasSuccess = results.some(
            (result) => result.status === "fulfilled"
          );

          if (hasSuccess) {
            successCount += 1;
          } else {
            failCount += 1;
          }
        }

        if (successCount) {
          toast(
            `${successCount} product${
              successCount !== 1 ? "s" : ""
            } uploaded to ${target.label}`,
            "success"
          );
        }

        if (failCount) {
          toast(
            `${failCount} product${failCount !== 1 ? "s" : ""} failed`,
            "error"
          );
        }

        clearSelection();
        await reload();
      } catch (err) {
        toast(getErrorMessage(err, "Bulk upload failed"), "error");
      } finally {
        setBulkUploading("");
      }
    },
    [selectedProducts, toast, uploadOneChannel, clearSelection, reload]
  );

  const adjustInventory = useCallback(
    async (product) => {
      const quantityDelta = Number(adjustments[product.id] || 0);

      if (!quantityDelta) return;

      try {
        const response = await api.post(
          `/products/${product.id}/inventory-adjustment`,
          {
            quantityDelta,
            reason: "Admin manual adjustment",
          }
        );

        if (!response?.product) {
          throw new Error("Inventory adjustment response was invalid.");
        }

        updateProductInTable(response.product);

        setAdjustments((current) => ({
          ...current,
          [product.id]: "",
        }));

        toast(`Inventory updated for ${product.sku}`, "success");
      } catch (err) {
        toast(getErrorMessage(err, "Could not update inventory"), "error");
      }
    },
    [adjustments, toast, updateProductInTable]
  );

  const applyJukiIncrease = useCallback(async () => {
    try {
      const response = await api.post("/products/price-increase", {
        brandLine: "Juki",
        percent: Number(pricePercent),
      });

      toast(response?.message || "Price increase applied", "success");
      await reload();
    } catch (err) {
      toast(getErrorMessage(err, "Could not apply price increase"), "error");
    }
  }, [pricePercent, reload, toast]);

  useEffect(() => {
    if (
      !categorySyncJob?.id ||
      !["pending", "running"].includes(categorySyncJob.status)
    ) {
      return undefined;
    }

    let stopped = false;
    let timerId;

    const poll = async () => {
      try {
        const response = await api.get(
          `/channels/ebay/categories/sync-products/${categorySyncJob.id}`
        );

        if (stopped || !response?.job) return;

        const nextJob = response.job;
        setCategorySyncJob(nextJob);

        if (nextJob.status === "completed") {
          toast(
            nextJob.message || "eBay category sync completed.",
            Number(nextJob.failed || 0) > 0 ? "warning" : "success"
          );
          await reload();
          return;
        }

        if (nextJob.status === "paused") {
          toast(
            nextJob.message ||
              "eBay category sync paused. Run it again after the eBay limit resets.",
            "warning"
          );
          await reload();
          return;
        }

        if (nextJob.status === "failed") {
          toast(nextJob.message || "eBay category sync failed.", "error");
          return;
        }

        timerId = window.setTimeout(poll, 1500);
      } catch (err) {
        if (!stopped) {
          toast(
            getErrorMessage(err, "Could not read eBay category sync progress"),
            "error"
          );
        }
      }
    };

    timerId = window.setTimeout(poll, 500);

    return () => {
      stopped = true;
      window.clearTimeout(timerId);
    };
  }, [categorySyncJob?.id, categorySyncJob?.status, reload, toast]);

  const exportProductsCsv = useCallback(() => {
    const rows = [
      [
        "SKU",
        "Product",
        "Brand",
        "Price",
        "Stock",
        "Channels",
        "Supplier",
        "Status",
      ],
      ...products.map((product) => [
        product.sku,
        product.name,
        product.brandLine,
        Number(product.price || 0).toFixed(2),
        product.stock,
        Array.isArray(product.channels) ? product.channels.join("|") : "",
        product.supplier,
        product.status,
      ]),
    ];

    const csv = rows
      .map((row) =>
        row
          .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "products-inventory-current-page.csv";
    link.click();

    URL.revokeObjectURL(url);

    toast("Product CSV exported", "success");
  }, [products, toast]);

  return (
    <div className="p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="mb-2 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#4391d1]">
            Inventory Command
          </p>

          <h1 className="text-3xl font-black text-gray-900">
            Products & Inventory
          </h1>

          {/* <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
            Manage SKUs, stock, supplier links, marketplace uploads, and
            inventory updates from one clean table.
          </p> */}
        </div>

        {/* <WorkflowToolbar
          actions={[
            {
              label: "Add Product",
              primary: true,
              onClick: () => navigate("/dashboard/products/new"),
            },
            {
              label: "Export CSV",
              onClick: exportProductsCsv,
            },
            {
              label: "Refresh",
              onClick: async () => {
                await loadConnectedChannels();
                await reload();
              },
            },
          ]}
        /> */}
      </div>

      {/* <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <InsightCard
          title="Total SKUs"
          value={pagination.totalItems}
          note="Matching current filter"
        />

        <InsightCard
          title="Low Stock"
          value={stats.lowStockCount}
          note="Below threshold globally"
          tone="warn"
        />

        <InsightCard
          title="Selected"
          value={selectedProductIds.size}
          note="Ready for bulk upload"
        />

        <InsightCard
          title="Connected"
          value={
            connectedChannels
              .map((channel) => PRODUCT_CHANNEL_LABELS[channel] || channel)
              .join(", ") || "None"
          }
          note={`Mapped channels on page: ${mappedChannels}`}
          tone="good"
        />
      </div> */}

      <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-5">
          <div>
            <h2 className="text-lg font-black text-gray-900">Product Table</h2>

            {!loading && (
              <p className="mt-0.5 text-xs text-gray-400">
                {pagination.totalItems} product
                {pagination.totalItems !== 1 ? "s" : ""}
                {activeFilter !== "All" ? ` - ${activeFilter}` : ""}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <QuickFilterDropdown active={activeFilter} onFilterChange={setActiveFilter} />

            <button
              className="inline-flex items-center gap-2 rounded-xl bg-[#4391d1] px-3 py-2 text-xs font-black text-white hover:bg-blue-600"
              onClick={() => navigate("/dashboard/products/new")}
              type="button"
            >
              <PackagePlus size={14} /> Add Product
            </button>

            <button
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]"
              onClick={exportProductsCsv}
              type="button"
            >
              <Download size={14} /> Export CSV
            </button>

            <button
              className="inline-flex cursor-not-allowed items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-black text-gray-400"
              disabled
              title="CSV import coming soon."
              type="button"
            >
              <Upload size={14} /> Import CSV
            </button>

            <button
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]"
              onClick={async () => {
                await loadConnectedChannels();
                await reload();
              }}
              type="button"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        <EbayCategorySyncPanel job={categorySyncJob} />
        <BulkUploadBar
          selectedCount={selectedProductIds.size}
          uploadTargets={uploadTargets}
          bulkUploading={bulkUploading}
          onBulkUpload={bulkUploadToTarget}
          onClearSelection={clearSelection}
        />

        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-400">
              <tr>
                <th className="w-10 whitespace-nowrap px-4 py-3 font-black">
                  <input
                    checked={allCurrentPageSelected}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-[#4391d1] focus:ring-[#4391d1]"
                    disabled={!products.length || loading}
                    onChange={toggleSelectCurrentPage}
                    type="checkbox"
                  />
                </th>

                <th className="whitespace-nowrap px-4 py-3 font-black">
                  Product
                </th>

                <th className="whitespace-nowrap px-4 py-3 font-black">
                  Marketplace
                </th>

                <th className="whitespace-nowrap px-4 py-3 font-black">
                  Status / Brand
                </th>

                <th className="whitespace-nowrap px-4 py-3 font-black">
                  Price
                </th>

                <th className="w-24 whitespace-nowrap px-3 py-3 font-black">
                  Stock
                </th>

                <th className="w-12 px-4 py-3 font-black">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td
                    className="px-6 py-10 text-center text-sm font-bold text-gray-400"
                    colSpan={7}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw size={14} className="animate-spin" />
                      Loading products...
                    </div>
                  </td>
                </tr>
              )}

              {!loading && products.length === 0 && (
                <tr>
                  <td
                    className="px-6 py-10 text-center text-sm font-bold text-gray-400"
                    colSpan={7}
                  >
                    {activeFilter !== "All"
                      ? "No products match this filter. Try changing the filter."
                      : "No products yet. Add your first product to get started."}
                  </td>
                </tr>
              )}

              {!loading &&
                products.map((product) => (
                  <ProductRow
                    key={product.id}
                    product={product}
                    adjustment={adjustments[product.id]}
                    checked={selectedProductIds.has(product.id)}
                    connectedChannels={connectedChannels}
                    uploadTargets={uploadTargets}
                    uploadingKey={uploadingKey}
                    onToggleSelect={toggleSelectProduct}
                    onAdjustmentChange={setAdjustments}
                    onAdjustInventory={adjustInventory}
                    onDuplicate={duplicateProduct}
                    onEdit={editProduct}
                    onDelete={deleteProduct}
                    onUploadProduct={uploadProductToTarget}
                  />
                ))}
            </tbody>
          </table>
        </div>

        <PaginationControls pagination={pagination} />
      </section>

      <section className="mt-6 grid gap-5 xl:grid-cols-[1fr_360px]">
        <div />
        <PriceUpdatePanel
          percent={pricePercent}
          onPercentChange={setPricePercent}
          onApply={applyJukiIncrease}
        />
      </section>
    </div>
  );
}
